import { Trans } from '@lingui/react/macro';
import { FixedBottomBarContainer, NoddiButton, useIsWithinTailWindBreakpoint } from 'noddi-ui-web';
import { ReactNode, useState } from 'react';
import { FlowBackButton } from '../../Layouts/FlowBackButton';

/** Next-button props a booking step screen hands down to its bottom bar. */
export type BookingFlowNextButtonProps = {
  onNextClicked?: () => void;
  disableNextButton?: boolean;
  hideNextButton?: boolean;
  nextButtonLoading?: boolean;
};

type Props = BookingFlowNextButtonProps & {
  /** The flow's first step has nowhere to go back to. */
  hideBackButton?: boolean;
  /** Rendered after Back, before the control row, e.g. the contact chat widget. */
  leading?: ReactNode;
  /** A full-width row above the controls, e.g. the picked time on a phone. */
  summary?: ReactNode;
  /** Selection chips: on the left of the row on xs, beside Next above xs. */
  controls?: ReactNode;
  /** Keeps `controls` beside Next on xs too, for a control narrow enough to share Next's side. */
  keepControlsBesideNext?: boolean;
  /** Shown when Next is pressed while `disableNextButton` is set. */
  renderValidationModal: (props: { open: boolean; onClose: () => void }) => ReactNode;
};

/**
 * Shared bottom bar for mobile and stationary booking steps: keeps the chips and
 * Next on one row and owns the "pressed Next too early" validation modal state.
 */
export const BookingFlowBottomBar = ({
  onNextClicked,
  disableNextButton,
  hideNextButton,
  nextButtonLoading,
  hideBackButton,
  leading,
  summary,
  controls,
  keepControlsBesideNext = false,
  renderValidationModal
}: Props) => {
  const isXs = useIsWithinTailWindBreakpoint('xs');
  const controlsBesideNext = keepControlsBesideNext || !isXs;
  const [showValidationModal, setShowValidationModal] = useState(false);

  const handleNextClick = () => {
    if (disableNextButton) {
      setShowValidationModal(true);
    } else {
      onNextClicked?.();
    }
  };

  return (
    <FixedBottomBarContainer>
      <div className='flex w-full flex-col gap-3'>
        {summary}

        <div className='flex items-center justify-between gap-2'>
          {!hideBackButton && <FlowBackButton />}
          {leading}

          {/* min-w-0 lets a long chip truncate instead of pushing Next off a narrow phone. */}
          <div className='flex w-full min-w-0 items-center justify-between gap-2'>
            {!controlsBesideNext && controls}

            <div className='ml-auto flex items-center gap-2'>
              {controlsBesideNext && controls}

              {!hideNextButton && (
                <NoddiButton loading={nextButtonLoading} onClick={handleNextClick} endIcon='ArrowRight'>
                  <Trans>Next</Trans>
                </NoddiButton>
              )}
            </div>
          </div>
        </div>
      </div>

      {renderValidationModal({ open: showValidationModal, onClose: () => setShowValidationModal(false) })}
    </FixedBottomBarContainer>
  );
};
